import React, { useCallback, useEffect, useState } from "react";
import Icons from "../icons";
import { JiraStatusBadge, PriorityBadge } from "../jiraBadges";
import * as Loader from "./Loader";
import ConfirmModal from "./ConfirmModal";
import ErrorDialog from "./ErrorDialog";
import JiraConnectDialog from "./JiraConnectDialog";
import RunVariablesDialog from "./RunVariablesDialog";
import ForYouTestRuns from "./forYou/ForYouTestRuns";
import ExecutionResultView from "./ExecutionResultView";
import { ACTIVE_RUN, IconButton, LiveRunModal, runChip } from "./TestRunsPanel";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

/*
 * For you — what Jira assigns to the signed-in user: Test Executions (open
 * first), tests, stories and epics.
 *
 * The user links their own Jira account once (JiraConnectDialog). Cortex keeps
 * calling Jira with its service account; the link decides whose assigned work
 * is listed and who is named in Jira on what Cortex writes.
 *
 * A Test Execution opens to its tests. A test with an Artifact is executed
 * straight into that Test Execution (no new one is made in Jira); a test without
 * one opens Create Artifact on its story and comes back here.
 */

/* Jira's own order, top down. Only Test Runs is shown for now: the others are
   hidden (drop `hidden` to bring one back), and the screen opens on the first
   shown tab. */
const TABS = [
  { id: "epics", label: "Epics", Icon: Icons.JiraEpic, hidden: true },
  { id: "stories", label: "Stories", Icon: Icons.JiraStory, hidden: true },
  { id: "tests", label: "Tests", Icon: Icons.JiraXrayTest, hidden: true },
  /* No "Executions" tab: it ran Artifacts into a Jira Test Execution
     (executions/adopt) and wrote the result without the formal gates. That
     is retired — Test Runs below is the one formal (evidence) path. */
  /* The Xray test runs assigned to the user: execute, review, submit (its own
     workflow and data — forYou/ForYouTestRuns.js). */
  { id: "test_runs", label: "Test Runs", Icon: Icons.JiraXrayTest, own: true },
];
const SHOWN_TABS = TABS.filter((t) => !t.hidden);
/* The overview (GET for-you) feeds only the tabs that aren't `own`. */
const NEEDS_OVERVIEW = SHOWN_TABS.some((t) => !t.own);

async function getJson(url, init) {
  const res = await fetch(url, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const d = body?.detail;
    const err = new Error((typeof d === "string" ? d : d?.message) || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return body?.data;
}

function IssueKey({ issue, Icon }) {
  return (
    <a
      href={issue.url}
      target="_blank"
      rel="noreferrer"
      title="Open in Jira"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex flex-none items-center gap-1 text-sm font-semibold text-blue-700 hover:underline"
    >
      {Icon && <Icon className="h-4 w-4" />}
      {issue.key}
    </a>
  );
}

/* One assigned issue (test, story or epic). */
function IssueCard({ issue, Icon }) {
  return (
    <li className="rounded-lg border border-slate-200 bg-card px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <IssueKey issue={issue} Icon={Icon} />
        <JiraStatusBadge status={issue.status} category={issue.status_category} />
        <PriorityBadge priority={issue.priority} />
      </div>
      <p className="mt-1 text-sm text-slate-800">{issue.summary}</p>
      {issue.parent_key && (
        <p className="mt-1 text-xs text-slate-500">
          In {issue.parent_key}
          {issue.parent_summary ? ` · ${issue.parent_summary}` : ""}
        </p>
      )}
    </li>
  );
}

/* Executed = Jira has the Test Execution in a Done status: the "Executed"
   section. Nothing is run or created there. */
/* Executed (the server decides `executed`): Jira has it Done, or every test
   in it has a finished Cortex run — Xray results don't move its Jira status,
   so a run Cortex finished can still read "To Do" there. */
const isExecuted = (te) => (te.executed !== undefined ? Boolean(te.executed) : te.status_category === "done");

/* A test inside a Test Execution: its Jira status and priority, its last
   Cortex result here, and — only while the Test Execution is still to be
   executed — Create Artifact when it has none. Tests are not executed one by
   one; the Test Execution is. */
function TestRow({ t, busy, onCreate, onOpen, canCreate = true, last = false }) {
  const latest = t.executions?.[0] || null;
  const ran = !!latest && latest.run_status !== "ready";
  const finished = ran && !ACTIVE_RUN.has(latest.run_status);
  const chip = ran ? runChip(latest) : null;
  return (
    <li className="relative flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 pl-20 pr-4">
      {/* Tree branch from the Test Execution above: the trunk drops from under
          its icon (52px in) and runs on past every test but the last, which
          ends in an elbow; the test itself sits a level further in. */}
      <span
        aria-hidden="true"
        className={`absolute left-[3.25rem] top-0 w-px bg-slate-300 ${last ? "h-[1.4rem]" : "bottom-0"}`}
      />
      <span aria-hidden="true" className="absolute left-[3.25rem] top-[1.4rem] h-px w-5 bg-slate-300" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-800">
            <Icons.JiraXrayTest className="h-4 w-4" />
            {t.key}
          </span>
          <JiraStatusBadge status={t.status} category={t.status_category} />
          <PriorityBadge priority={t.priority} />
        </div>
        <p className="mt-0.5 text-sm text-slate-700">{t.summary}</p>
        {latest?.xray_push_error && (
          <p className="mt-1 text-xs text-rose-700">Jira: {latest.xray_push_error}</p>
        )}
      </div>
      <div className="flex flex-none items-center gap-2">
        {chip && (
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${chip.cls}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} />
            {chip.label}
          </span>
        )}
        {/* 👁 this test's result, with each step's screenshots. */}
        {finished && (
          <IconButton title={`See the result of ${t.key}, with each step's screenshots`} onClick={() => onOpen(latest.executionkey)}>
            <Icons.Eye className="h-4 w-4" />
          </IconButton>
        )}
        {!t.artifact && canCreate && (
          <button
            type="button"
            onClick={() => onCreate(t)}
            disabled={!!busy}
            title="This test has no Artifact yet. Create it, then execute the Test Execution."
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-300 bg-brand-50 px-3 py-1.5 text-sm font-semibold text-brand-700 hover:bg-brand-100 disabled:opacity-50"
          >
            <Icons.Plus className="h-4 w-4" />
            Create Artifact
          </button>
        )}
      </div>
    </li>
  );
}

/* A Test Execution card; opening it loads its tests. It behaves like a run
   card on the Artifacts screen: ▶ executes it straight away (every test in it
   that has an Artifact); while it runs, its chip, Watch and Stop; once it has
   run, its result chip and 👁 for the result page. Under "Executed" there is
   no ▶ and no Create Artifact. */
function ExecutionCard({ te, open, onToggle, detail, jiraName, rowBusy, onExecute, onWatch, onStop, onOpen, onCreate }) {
  const tests = detail?.data?.tests || [];
  const runs = tests.map((t) => t.executions?.[0]).filter((ex) => ex && ex.run_status !== "ready");
  const activeRun = runs.find((ex) => ACTIVE_RUN.has(ex.run_status));
  const finishedRuns = runs
    .filter((ex) => !ACTIVE_RUN.has(ex.run_status))
    .sort((a, b) => String(b.finished_at || b.createddate || "").localeCompare(String(a.finished_at || a.createddate || "")));
  /* The card's chip: the running one, else a failure if any test failed, else the latest result. */
  const shown = activeRun || finishedRuns.find((ex) => ex.run_status === "failed") || finishedRuns[0] || null;
  const chip = shown ? runChip(shown) : null;
  const live = activeRun?.live;
  const pct = live?.total ? Math.round((Math.max(0, live.step - 1) / live.total) * 100) : 0;
  const executed = isExecuted(te);
  const noneReady = !!detail?.data && !tests.some((t) => t.artifact);
  return (
    <li
      className={[
        "overflow-hidden rounded-lg border bg-card shadow-sm transition",
        activeRun ? "border-blue-200 ring-1 ring-blue-500/20" : "border-slate-200",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 pr-4 hover:bg-slate-50">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${open ? "Hide" : "Show"} the tests in ${te.key}`}
          className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left"
        >
          {open ? (
            <Icons.ChevronDown className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
          ) : (
            <Icons.ChevronRight className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
          )}
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <IssueKey issue={te} Icon={Icons.JiraTestExecution} />
              <JiraStatusBadge status={te.status} category={te.status_category} />
              <PriorityBadge priority={te.priority} />
            </span>
            <span className="mt-1 block text-sm text-slate-800">{te.summary}</span>
          </span>
        </button>
        {chip && (
          <>
            <span className={`hidden flex-none items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset sm:inline-flex ${chip.cls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} />
              {chip.label}
            </span>
            <span className="mx-0.5 hidden h-6 w-px flex-none bg-slate-200 sm:block" aria-hidden="true" />
          </>
        )}
        {activeRun ? (
          <>
            <IconButton title="Watch the live browser" onClick={() => onWatch(activeRun)}>
              <Icons.Activity className="h-4 w-4" />
            </IconButton>
            <IconButton title="Stop this execution" onClick={() => onStop(te, runs.filter((ex) => ACTIVE_RUN.has(ex.run_status)))} danger>
              <Icons.X className="h-4 w-4" />
            </IconButton>
          </>
        ) : (
          finishedRuns[0] && (
            <IconButton title="See the result, with each step's screenshots" onClick={() => onOpen((shown || finishedRuns[0]).executionkey)}>
              <Icons.Eye className="h-4 w-4" />
            </IconButton>
          )
        )}
        {activeRun || executed ? null : (
          <button
            type="button"
            onClick={() => onExecute(te)}
            disabled={noneReady || !!rowBusy[te.key]}
            title={
              noneReady
                ? "No test in this Test Execution has an Artifact yet — create one first"
                : `Execute ${te.key} — the results go to this Test Execution in Jira${jiraName ? `, as ${jiraName}` : ""}`
            }
            aria-label={`Execute ${te.key}`}
            className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-sm shadow-emerald-600/30 ring-1 ring-inset ring-emerald-700/20 transition hover:from-emerald-600 hover:to-emerald-700 active:scale-[0.95] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icons.Play className="ml-0.5 h-4 w-4" />
          </button>
        )}
      </div>
      {activeRun?.run_status === "running" && live?.total > 0 && (
        <div className="px-4 pb-3 pl-[3.25rem]">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      {open && (
        <div className="-mt-1">
          {detail?.error && <p className="px-4 py-3 text-sm text-rose-700">{detail.error}</p>}
          {detail?.data && tests.length === 0 && (
            <p className="px-4 py-3 text-sm text-slate-500">This Test Execution has no tests yet.</p>
          )}
          {tests.length > 0 && (
            <ul aria-label={`Tests in ${te.key}`} className="pb-2">
              {tests.map((t, i) => (
                <TestRow
                  key={t.key}
                  t={t}
                  last={i === tests.length - 1}
                  canCreate={!executed && !activeRun}
                  busy={rowBusy[`${te.key}:${t.key}`]}
                  onOpen={onOpen}
                  onCreate={(test) => onCreate(te, test)}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

export default function ForYouView({ userkey, refreshSeq = 0, onCreateArtifact }) {
  const [link, setLink] = useState(null); // {connected, ready, display_name, …}
  const [linkError, setLinkError] = useState("");
  const [connectOpen, setConnectOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(SHOWN_TABS[0].id);
  /* Bumped by Refresh, so the Test Runs tab reloads too. */
  const [runsSeq, setRunsSeq] = useState(0);

  const [openTe, setOpenTe] = useState(() => new Set());
  const [details, setDetails] = useState({}); // te key -> {loading, data, error}
  const [rowBusy, setRowBusy] = useState({});
  const [watching, setWatching] = useState(null);
  /* ▶ waiting for its variables' values: {te, runnable, tests}, or null. */
  const [runVars, setRunVars] = useState(null);
  /* 👁: the execution whose result page is shown, here on For you. */
  const [resultKey, setResultKey] = useState(null);
  const [failure, setFailure] = useState(null); // {title, message}

  const loadLink = useCallback(async () => {
    if (!userkey) return;
    setLinkError("");
    try {
      setLink(await getJson(`${API_BASE_URL}for-you/jira-link?userkey=${encodeURIComponent(userkey)}`));
    } catch (e) {
      setLinkError(e.message);
    }
  }, [userkey]);

  const loadOverview = useCallback(async ({ quiet = false } = {}) => {
    if (!userkey) return;
    if (!quiet) setLoading(true);
    setError("");
    try {
      setData(await getJson(`${API_BASE_URL}for-you?userkey=${encodeURIComponent(userkey)}`));
    } catch (e) {
      if (e.status === 409) setLink((l) => ({ ...(l || {}), connected: false }));
      else setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [userkey]);

  const loadTe = useCallback(async (key, { quiet = false } = {}) => {
    setDetails((d) => ({ ...d, [key]: { ...(d[key] || {}), loading: !quiet, error: quiet ? d[key]?.error : "" } }));
    try {
      const out = await getJson(`${API_BASE_URL}for-you/test-executions/${encodeURIComponent(key)}`);
      setDetails((d) => ({ ...d, [key]: { loading: false, data: out, error: "" } }));
      return out;
    } catch (e) {
      setDetails((d) => ({ ...d, [key]: { ...(d[key] || {}), loading: false, error: e.message } }));
      return null;
    }
  }, []);

  useEffect(() => {
    loadLink();
  }, [loadLink]);

  /* Connected = Jira email + API token given (on the profile, or here). A
     link made before tokens were asked counts as not connected yet. */
  const connected = !!link?.connected && !!link?.ready;
  useEffect(() => {
    if (connected && NEEDS_OVERVIEW) loadOverview();
  }, [connected, loadOverview]);

  /* Back from Create Artifact: the open Test Executions show the new Artifact. */
  useEffect(() => {
    if (!refreshSeq) return;
    openTe.forEach((k) => loadTe(k, { quiet: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSeq]);

  /* While a run is queued or running in an open Test Execution, keep it fresh. */
  const anyActive = [...openTe].some((k) =>
    (details[k]?.data?.tests || []).some((t) => ACTIVE_RUN.has(t.executions?.[0]?.run_status)),
  );
  useEffect(() => {
    if (!anyActive) return undefined;
    const timer = setInterval(() => openTe.forEach((k) => loadTe(k, { quiet: true })), 4000);
    return () => clearInterval(timer);
  }, [anyActive, openTe, loadTe]);

  /* A run here just finished: re-read the list (quietly), so a Test
     Execution whose every test has now run moves under "Executed". */
  const wasActive = React.useRef(false);
  useEffect(() => {
    if (wasActive.current && !anyActive) loadOverview({ quiet: true });
    wasActive.current = anyActive;
  }, [anyActive, loadOverview]);

  const toggleTe = (key) => {
    setOpenTe((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else {
        next.add(key);
        if (!details[key]?.data) loadTe(key);
      }
      return next;
    });
  };

  const busyKey = (te, t) => `${te.key}:${t.key}`;
  const setBusy = (k, v) =>
    setRowBusy((b) => {
      const next = { ...b };
      if (v) next[k] = v;
      else delete next[k];
      return next;
    });

  /* ▶ on a Test Execution, straight away (as on the Artifacts screen): each
     test in it with an Artifact runs as a Cortex execution on that same Jira
     Test Execution (nothing new is made in Jira); the runs queue and report
     there one by one, and the card shows their progress. */
  const execute = async (te) => {
    setBusy(te.key, "execute");
    const loaded = details[te.key]?.data || (await loadTe(te.key));
    if (!loaded) {
      setBusy(te.key, ""); // the load error shows on the card
      return;
    }
    const runnable = loaded.tests.filter((t) => t.artifact);
    if (runnable.length === 0) {
      setBusy(te.key, "");
      setFailure({
        title: `Nothing to execute in ${te.key}`,
        message: "None of its tests has an Artifact yet. Open it and use Create Artifact on a test first.",
      });
      return;
    }
    /* Artifacts that keep {VARIABLES}: one dialog asks for all of them
       (shared names once), for this run only. */
    if (runnable.some((t) => (t.artifact.variables || []).length)) {
      setBusy(te.key, "");
      setRunVars({
        te,
        runnable,
        tests: runnable.map((t) => ({ key: t.key, summary: t.summary, variables: t.artifact.variables || [] })),
      });
      return;
    }
    await runTests(te, runnable, {});
  };

  /* Retired: running Artifacts into a Jira Test Execution from here wrote
     the result without the formal gates (executions/adopt now refuses).
     Evidence is recorded from Test Runs. */
  const runTests = async (te) => {
    setBusy(te.key, "");
    setFailure({
      title: `${te.key} is executed from Test Runs`,
      message: "Running a Test Execution from here is retired. Open Test Runs: each run assigned to you is executed, reviewed and submitted there.",
    });
  };

  /* ✕ on a running Test Execution: stop each of its runs that is queued or
     running (the same call as Stop on the Artifacts screen). */
  const stopRuns = async (te, active) => {
    await Promise.all(
      active.map((ex) =>
        fetch(`${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}/cancel`, { method: "POST" }).catch(() => {}),
      ),
    );
    loadTe(te.key, { quiet: true });
  };

  /* Create Artifact: open Create Artifact on the test's story, with only this
     test selected; it comes back here when saved. */
  const createArtifact = async (te, test) => {
    const k = busyKey(te, test);
    setBusy(k, "create");
    try {
      const s = await getJson(`${API_BASE_URL}for-you/tests/${encodeURIComponent(test.key)}/story`);
      onCreateArtifact?.({
        projectKey: s.project_key,
        epicKey: s.epic_key,
        epicSummary: s.epic_summary,
        story: { key: s.story_key, summary: s.story_summary, epic_key: s.epic_key, epic_summary: s.epic_summary },
        xrayTestKeys: [test.key],
      });
    } catch (e) {
      setFailure({ title: `Can't create an Artifact for ${test.key}`, message: e.message });
    } finally {
      setBusy(k, "");
    }
  };

  const disconnect = async () => {
    setConfirmDisconnect(false);
    try {
      setLink(await getJson(`${API_BASE_URL}for-you/jira-link?userkey=${encodeURIComponent(userkey)}`, { method: "DELETE" }));
      setData(null);
    } catch (e) {
      setFailure({ title: "Could not disconnect", message: e.message });
    }
  };

  const jiraName = link?.display_name || "";

  /* One guide loader for every wait on this screen (no spinners). Background
     polls (quiet loads) don't show it. */
  const loadingTe = Object.keys(details).find((k) => details[k]?.loading);
  const busyRow = Object.entries(rowBusy)[0];
  let wait = null;
  if (!link && !linkError) {
    wait = { label: "Checking your Jira connection…", sublabel: "Finding the Jira account linked to you" };
  } else if (loading) {
    wait = { label: "Loading your work…", sublabel: "Fetching the Test Executions, tests, stories and epics Jira assigns to you" };
  } else if (loadingTe) {
    wait = { label: "Loading the Test Execution…", sublabel: `Fetching the tests in ${loadingTe} and their Artifacts` };
  } else if (busyRow) {
    const [teKey, testKey] = busyRow[0].split(":");
    wait = busyRow[1] === "create"
      ? { label: "Opening Create Artifact…", sublabel: `Fetching the story and epic of ${testKey}` }
      : { label: "Starting the execution…", sublabel: `Queueing the tests of ${teKey} in Cortex` };
  }
  const counts = Object.fromEntries(TABS.map((t) => [t.id, (data?.[t.id] || []).length]));
  const executions = data?.test_executions || [];
  const openExecs = executions.filter((e) => !isExecuted(e));
  const doneExecs = executions.filter((e) => isExecuted(e));

  const renderExecs = (list) => (
    <ul className="space-y-3">
      {list.map((te) => (
        <ExecutionCard
          key={te.key}
          te={te}
          open={openTe.has(te.key)}
          onToggle={() => toggleTe(te.key)}
          detail={details[te.key]}
          jiraName={jiraName}
          rowBusy={rowBusy}
          onExecute={execute}
          onWatch={setWatching}
          onStop={stopRuns}
          onOpen={setResultKey}
          onCreate={createArtifact}
        />
      ))}
    </ul>
  );

  /* 👁: the result page, the same one the Artifacts screen shows, here on For
     you. The list behind it (open cards, tab, loaded tests) is kept, so Back
     returns exactly where the user was. */
  if (resultKey) {
    return (
      <ExecutionResultView
        executionkey={resultKey}
        backLabel="For you"
        onBack={() => {
          setResultKey(null);
          openTe.forEach((k) => loadTe(k, { quiet: true }));
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">For you</h1>
          <p className="mt-1 text-sm text-slate-600">Test runs assigned to you in Jira</p>
        </div>
        {connected && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
              <Icons.Check className="h-3.5 w-3.5" /> Jira: {jiraName}
            </span>
            <button
              type="button"
              onClick={() => {
                if (NEEDS_OVERVIEW) loadOverview();
                openTe.forEach((k) => loadTe(k, { quiet: true }));
                setRunsSeq((n) => n + 1);
              }}
              disabled={loading}
              aria-label="Refresh"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Icons.Refresh className="h-4 w-4" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setConfirmDisconnect(true)}
              className="rounded-lg px-3 py-1.5 text-sm font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      {linkError && <p role="alert" className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">{linkError}</p>}


      {link && !connected && (
        <div className="rounded-xl border border-slate-200 bg-card p-6 shadow-sm">
          <div className="flex flex-wrap items-start gap-4">
            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-700">
              <Icons.Link className="h-6 w-6" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-slate-900">Connect your Jira account</h2>
              <p className="mt-1 text-sm text-slate-600">
                Give the email you sign in to Jira with and your own Jira API token (you can also do this on your
                profile). For you then lists what Jira assigns you, what you make in Cortex names you in Jira
                (Reporter, Assignee, Executed By), and your test runs are worked in Jira with your own rights.
              </p>
              <button
                type="button"
                onClick={() => setConnectOpen(true)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
              >
                <Icons.Link className="h-4 w-4" /> Connect Jira
              </button>
            </div>
          </div>
        </div>
      )}

      {connected && (
        <>
          <div role="tablist" aria-label="Assigned to you" className="flex flex-wrap gap-1 border-b border-slate-200">
            {SHOWN_TABS.map(({ id, label, Icon, own }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={[
                  "-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold",
                  tab === id ? "border-brand-600 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-800",
                ].join(" ")}
              >
                <Icon className="h-4 w-4" />
                {label}
                {data && !own && <span className="rounded-full bg-slate-100 px-1.5 text-[11px] text-slate-600">{counts[id]}</span>}
              </button>
            ))}
          </div>

          {error && <p role="alert" className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}

          {data && tab === "test_executions" && (
            <div className="space-y-6">
              {executions.length === 0 && <p className="text-sm text-slate-500">No Test Executions are assigned to you in Jira.</p>}
              {openExecs.length > 0 && (
                <section aria-label="Test Executions to execute" className="space-y-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">To Execute - {openExecs.length}</h2>
                  {renderExecs(openExecs)}
                </section>
              )}
              {doneExecs.length > 0 && (
                <section aria-label="Executed Test Executions" className="space-y-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Executed - {doneExecs.length}</h2>
                  {renderExecs(doneExecs)}
                </section>
              )}
            </div>
          )}

          {tab === "test_runs" && (
            <ForYouTestRuns
              userkey={userkey}
              onLinkChange={setLink}
              refreshSeq={refreshSeq + runsSeq}
              onCreateArtifact={onCreateArtifact}
            />
          )}

          {data && tab !== "test_executions" && tab !== "test_runs" && (
            <>
              {counts[tab] === 0 ? (
                <p className="text-sm text-slate-500">Nothing of this kind is assigned to you in Jira.</p>
              ) : (
                <ul className="grid gap-3 md:grid-cols-2">
                  {data[tab].map((issue) => (
                    <IssueCard key={issue.key} issue={issue} Icon={TABS.find((t) => t.id === tab).Icon} />
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}

      {connectOpen && (
        <JiraConnectDialog
          userkey={userkey}
          onClose={() => setConnectOpen(false)}
          onConnected={(l) => {
            setConnectOpen(false);
            setLink(l);
          }}
        />
      )}

      {confirmDisconnect && (
        <ConfirmModal
          title="Disconnect Jira?"
          message={`For you will stop listing your Jira work, and Cortex will stop naming ${jiraName || "you"} in Jira until you connect again.`}
          confirmLabel="Disconnect"
          icon={Icons.Unlink}
          onCancel={() => setConfirmDisconnect(false)}
          onConfirm={disconnect}
        />
      )}

      {runVars && (
        <RunVariablesDialog
          title={`Values for ${runVars.te.key}`}
          tests={runVars.tests}
          userkey={userkey}
          onCancel={() => setRunVars(null)}
          onExecute={(values) => {
            const { te, runnable } = runVars;
            setRunVars(null);
            runTests(te, runnable, values);
          }}
        />
      )}

      {watching && (
        <LiveRunModal
          ex={watching}
          onClose={() => {
            setWatching(null);
            openTe.forEach((k) => loadTe(k, { quiet: true }));
          }}
          onStop={(ex) => fetch(`${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}/cancel`, { method: "POST" }).catch(() => {})}
        />
      )}

      {failure && <ErrorDialog title={failure.title} message={failure.message} onClose={() => setFailure(null)} />}

      {wait && <Loader.Overlay variant="guide" label={wait.label} sublabel={wait.sublabel} delay={0} />}
    </div>
  );
}
